// ============================================================================
// TwoFactorEnrollment
// Owner: Claude 3 (Frontend & UX).
// Purpose: 2FA landing — three cards (TOTP, WebAuthn, SMS).
//          QA-tier users see "Mandatory" badge on WebAuthn (SEC-021) and
//          cannot proceed with TOTP-only.
// URS rows: AUTH-002..006, SEC-021.
// ============================================================================
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../auth/SessionProvider';
import { useMyEnrolledFactors } from '../../api/twoFactorHooks';
import { TotpEnrollFlow } from './TotpEnrollFlow';
import { WebAuthnEnrollFlow } from './WebAuthnEnrollFlow';
import { SmsEnrollFlow } from './SmsEnrollFlow';

const QA_TIER_ROLES = new Set(['QA_APPROVER', 'DOC_CONTROLLER', 'QA_MANAGER']);

export function TwoFactorEnrollment() {
  const { t } = useTranslation();
  const { session } = useSession();
  const { data: enrolled } = useMyEnrolledFactors();
  const [active, setActive] = useState<'TOTP' | 'WEBAUTHN' | 'SMS' | null>(null);

  const isQaTier = (session?.roles ?? []).some((r) => QA_TIER_ROLES.has(r));
  const has = (f: 'TOTP' | 'WEBAUTHN' | 'SMS') =>
    (enrolled ?? []).some((e) => e.factor === f);

  return (
    <main style={{ padding: 24 }}>
      <h1>{t('twofa.heading')}</h1>

      <section style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', marginBlockStart: 24 }}>
        <Card
          title={t('twofa.totp.card_title')}
          enrolled={has('TOTP')}
          enrolledLabel={t('twofa.common.enrolled')}
          setupLabel={t('twofa.common.set_up')}
          onClick={() => setActive('TOTP')}
        />
        <Card
          title={t('twofa.webauthn.card_title')}
          mandatoryForQa={isQaTier}
          mandatoryLabel={t('twofa.webauthn.mandatory_for_qa')}
          enrolled={has('WEBAUTHN')}
          enrolledLabel={t('twofa.common.enrolled')}
          setupLabel={t('twofa.common.set_up')}
          onClick={() => setActive('WEBAUTHN')}
        />
        <Card
          title={t('twofa.sms.card_title')}
          enrolled={has('SMS')}
          enrolledLabel={t('twofa.common.enrolled')}
          setupLabel={t('twofa.common.set_up')}
          warning={isQaTier ? t('twofa.sms.weak_factor_warning') : undefined}
          onClick={() => setActive('SMS')}
        />
      </section>

      <section style={{ marginBlockStart: 32 }} aria-live="polite">
        {active === 'TOTP' && <TotpEnrollFlow onDone={() => setActive(null)} />}
        {active === 'WEBAUTHN' && <WebAuthnEnrollFlow onDone={() => setActive(null)} />}
        {active === 'SMS' && <SmsEnrollFlow onDone={() => setActive(null)} />}
      </section>
    </main>
  );
}

function Card(props: {
  title: string;
  enrolled: boolean;
  enrolledLabel: string;
  setupLabel: string;
  mandatoryForQa?: boolean;
  mandatoryLabel?: string;
  warning?: string;
  onClick: () => void;
}) {
  return (
    <div style={{
      border: '1px solid var(--color-border, #ccc)',
      borderRadius: 8,
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    }}>
      <h2 style={{ margin: 0 }}>{props.title}</h2>
      {props.mandatoryForQa && (
        <span aria-label={props.mandatoryLabel} style={{ fontWeight: 600 }}>
          {props.mandatoryLabel}
        </span>
      )}
      {props.warning && <span role="note">{props.warning}</span>}
      <button onClick={props.onClick}>
        {props.enrolled ? `${props.enrolledLabel} ✓` : props.setupLabel}
      </button>
    </div>
  );
}
